import {
  CreateButton,
  ExportButton,
  List,
  useSidebarState,
  useTranslate
} from "react-admin";
import * as User from "providers/user";
import { DownloadIcon, EmptyMenuIcon } from "assets/Icons";
import CustomEmptyList from "./CustomEmptyList";
import { useMediaQuery } from "@mui/material";
import { Theme } from "@emotion/react";

const CustomListActions = ({
  hasCreate,
  hasExport,
  title,
  createButtonText,
  createResource,
  moreActions,
  customExport
}: {
  hasCreate?: boolean;
  hasExport?: boolean;
  title?: string;
  createButtonText?: string;
  createResource?: string;
  moreActions?: any;
  customExport?: any;
}) => {
  const translate = useTranslate();
  const { user } = User.useUser();

  return (
    <div
      className={`w-full min-h-[80px] ${
        user.impersonate_company_id ? "mt-[30px]" : ""
      } flex justify-between items-center bg-mono-primaryBgColor px-4 xs:px-0`}
    >
      {title ? (
        <h4 className="font-['OptimaLTStd'] font-[600]">{title}</h4>
      ) : (
        <div />
      )}
      {moreActions}
      <div className="flex gap-[8px] items-center customListActionsButtonsWrapper">
        {hasExport !== false && (
          <ExportButton
            label={translate("common.export_to_csv")}
            icon={<DownloadIcon />}
          />
        )}
        {customExport && customExport}
        {hasCreate !== false && (
          <CreateButton resource={createResource} label={createButtonText} />
        )}
      </div>
    </div>
  );
};

const CustomList = ({
  children,
  title = "",
  emptyIcon = <EmptyMenuIcon />,
  emptyTitle = "No data yet",
  emptyListCreateSubtitle = "Do you want to add one?",
  createButtonText = "Create",
  hasCreate = true,
  exporter = true,
  actions,
  moreActions,
  empty,
  createResource,
  marginTop,
  customExport,
  reportingListsWidth = null,
  dashboardFullHeightAndWidth = false,
  ...rest
}: any) => {
  const { permissions } = User.useUser();
  const [open] = useSidebarState();
  const isSmall = useMediaQuery<Theme>("(max-width:640px)");
  const isMedium = useMediaQuery<Theme>("(max-width:1024px)");
  const isIpadSizeScreen = isMedium && !isSmall;

  return (
    <List
      actions={
        actions || (
          <CustomListActions
            title={title}
            hasExport={permissions.isArtist || !exporter ? false : true}
            createButtonText={createButtonText}
            hasCreate={hasCreate}
            createResource={createResource}
            moreActions={moreActions}
            customExport={customExport}
          />
        )
      }
      hasCreate={permissions.isArtist ? false : true}
      empty={
        empty === false ? (
          empty
        ) : (
          <div>
            {actions ? (
              actions
            ) : (
              <CustomListActions
                title={title}
                hasExport={permissions.isArtist ? false : true}
                createButtonText={createButtonText}
                hasCreate={hasCreate}
                createResource={createResource}
                moreActions={moreActions}
              />
            )}
            <CustomEmptyList
              emptyIcon={emptyIcon}
              emptyTitle={emptyTitle}
              emptyListCreateSubtitle={emptyListCreateSubtitle}
              createButtonText={createButtonText}
              hasCreate={hasCreate}
              createResource={createResource}
            />
          </div>
        )
      }
      {...rest}
      sx={{
        ...(dashboardFullHeightAndWidth && {
          height: "100%",
          display: "block",
          ".list-page ": {
            height: "100%",
            display: "block !important"
          },
          ".RaList-main": {
            height: "100%"
          },
          ".RaDatagrid-root": {
            height: "100%",
            backgroundColor: "#F6F4F1 !important"
          },
          ".RaDatagrid-tbody": {
            backgroundColor: "#FAF9F7 !important",
            borderRadius: "8px",
            "tr:last-child": {
              "& td": {
                borderBottom: "none"
              },
              "& td:first-child": {
                borderBottomLeftRadius: "8px"
              },
              "& td:last-child": {
                borderBottomRightRadius: "8px"
              }
            }
          },
          ".MuiCard-root": {
            backgroundColor: "#F6F4F1 !important"
          },
          ".RaDatagrid-tableWrapper": {
            height: "100%"
          },
          ".RaDatagrid-headerCell": {
            padding: "17px 8px !important"
          },
          ".RaDatagrid-headerCell:first-child": {
            padding: "17px 8px 17px 16px !important"
          },
          ".RaDatagrid-rowCell": {
            padding: "6px 8px !important"
          }
        }),
        marginTop: marginTop || "0px",
        ".RaList-content": {
          boxShadow: "none",
          borderRadius: "0px",
          ...(dashboardFullHeightAndWidth && {
            height: "100%",
            borderRadius: "8px",
            ".overflow-hidden": {
              height: "100%",
              borderRadius: "8px"
            }
          })
        },
        ".MuiTablePagination-selectLabel": {
          fontSize: "16px",
          fontFamily: "Inter",
          color: "rgba(48, 47, 46, 0.6)"
        },
        ".MuiTablePagination-select": {
          fontSize: "16px",
          fontFamily: "Inter",
          color: "rgba(48, 47, 46, 0.95)"
        },
        ".MuiTablePagination-selectIcon": {
          width: "24px"
        },
        ".MuiTablePagination-displayedRows": {
          fontSize: "16px",
          fontFamily: "Inter",
          color: "rgba(48, 47, 46, 0.95)"
        },
        ".MuiPaginationItem-page": {
          fontSize: "16px",
          fontFamily: "Inter",
          color: "rgba(48, 47, 46, 0.95)",
          minWidth: "32px",
          height: "32px",
          width: "32px",
          borderRadius: "16px"
        },
        ".MuiPaginationItem-previousNext": {
          fontSize: "22px",
          fontFamily: "Inter",
          color: "rgba(48, 47, 46, 0.95)",
          minWidth: "32px",
          height: "32px",
          width: "32px",
          borderRadius: "16px"
        },
        ".customListActionsButtonsWrapper .MuiButton-sizeSmall": {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "36px",
          backgroundColor: "#FAF9F7",
          color: "rgba(48, 47, 46, 0.95)",
          fontSize: "14px",
          lineHeight: "18px",
          fontWeight: 500,
          borderRadius: "18px",
          padding: "0px 16px",
          border: "1px solid rgba(48, 47, 46, 0.23)",
          textTransform: "none"
        },
        ".RaList-noResults": {
          width: "100%",
          height: "calc(100vh - 150px)"
        },
        ".RaList-actions": {
          alignItems: "center"
        },
        ".RaList-actions form": {
          flex: "none"
        },
        ".RaDatagrid-tableWrapper": {
          width: isSmall
            ? reportingListsWidth
            : !open
            ? `calc(100vw - 116px)`
            : `calc(100vw - ${isIpadSizeScreen ? 242 : 300}px)`,
          overflowX: "auto",
          ...(dashboardFullHeightAndWidth && {
            width: "100% !important",
            overflowX: "hidden",
            height: "100%"
          })
        }
      }}
    >
      {children}
    </List>
  );
};

export default CustomList;
